import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';

type TimerCallback = () => void | Promise<void>;

interface TimerAction {
	callback: TimerCallback;
	everySeconds: number;
	counter: number;
}

interface TimerContextValue {
	registerAction: (callback: TimerCallback, everySeconds: number) => () => void;
}

interface TimerProviderProps {
	intervalMs?: number;
	children: ReactNode;
}

const TimerContext = createContext<TimerContextValue | null>(null);

export function TimerProvider({ intervalMs = 1000, children }: TimerProviderProps): JSX.Element {
	const actionsRef = useRef<Set<TimerAction>>(new Set());

	const registerAction = (callback: TimerCallback, everySeconds: number): (() => void) => {
		const action: TimerAction = { callback, everySeconds, counter: 0 };
		actionsRef.current.add(action);
		return () => {
			actionsRef.current.delete(action);
		};
	};

	useEffect(() => {
		const timer = setInterval(() => {
			const actions = Array.from(actionsRef.current);
			for (const action of actions) {
				action.counter++;
				if (action.counter >= action.everySeconds) {
					action.counter = 0;
					try {
						action.callback();
					} catch (e) {
						console.error('Timer action error:', e);
					}
				}
			}
		}, intervalMs);

		return () => {
			clearInterval(timer);
		};
	}, [intervalMs]);

	return <TimerContext.Provider value={{ registerAction }}>{children}</TimerContext.Provider>;
}

export const useGlobalTimer = (): TimerContextValue => {
	const ctx = useContext(TimerContext);
	if (!ctx) {
		throw new Error('useGlobalTimer must be used inside TimerProvider');
	}
	return ctx;
};
